import React, { useEffect, useRef } from "react";

// Monochrome "topographic contour" shader. Domain-warped fbm noise drawn as
// anti-aliased iso-lines, drifting slowly, gently pulled by the cursor.
// Transparent canvas: works over any CSS background (light or dark).
const VERT = `#version 300 es
in vec2 p;
void main(){ gl_Position = vec4(p, 0.0, 1.0); }`;

const FRAG = `#version 300 es
precision highp float;
uniform vec2 uRes;
uniform float uTime;
uniform vec2 uMouse;      // 0..1, y up
uniform vec3 uFg;
uniform float uLines;
uniform float uStrength;
uniform float uFadeLeft;  // 0 = uniform, 1 = fade out towards the left (text side)
uniform float uOrder;     // <0 = unused, 0 = chaos ... 1 = calm parallel lines
out vec4 outColor;

float hash(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
float noise(vec2 p){
  vec2 i = floor(p), f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  return mix(mix(hash(i), hash(i + vec2(1,0)), f.x), mix(hash(i + vec2(0,1)), hash(i + vec2(1,1)), f.x), f.y);
}
float fbm(vec2 p){
  float a = 0.5, s = 0.0;
  for (int i = 0; i < 5; i++){ s += a * noise(p); p = p * 2.02 + 7.3; a *= 0.5; }
  return s;
}

void main(){
  vec2 frag = gl_FragCoord.xy;
  float asp = uRes.x / uRes.y;
  vec2 uv = frag / uRes.y;
  vec2 m = vec2(uMouse.x * asp, uMouse.y);

  float t = uTime * 0.05;
  bool on = uOrder >= 0.0;
  float c = on ? 1.0 - clamp(uOrder, 0.0, 1.0) : 0.0;
  vec2 q = uv * (on ? 1.15 + 1.5 * c : 1.15);
  vec2 w = vec2(fbm(q + vec2(0.0, t)), fbm(q + vec2(5.2, 1.3) - t));
  float wk = on ? 0.15 + 1.9 * c : 1.7;
  float n = fbm(q + wk * w + vec2(t * 0.6, -t * 0.4));

  // cursor: soft swell in the field so lines bend around the pointer
  float d = distance(uv, m);
  n += 0.16 * exp(-d * d * 5.0);

  float lines = on ? uLines * (0.75 + 0.9 * c) : uLines;
  float calm = on ? smoothstep(0.45, 1.0, uOrder) : 0.0;
  float v = mix(n * lines, (uv.y * 0.85 + n * 0.05) * uLines * 0.6, calm);
  float f = abs(fract(v) - 0.5);
  float fw = max(fwidth(v), 1e-4);
  float line = 1.0 - smoothstep(0.55 * fw, 1.5 * fw, f);
  // major lines slightly stronger for rhythm
  float major = step(0.5, fract(v / 4.0));
  line *= mix(0.55, 1.0, major);

  float x = frag.x / uRes.x;
  float mask = mix(1.0, smoothstep(0.05, 0.85, x), uFadeLeft);
  float edge = smoothstep(0.0, 0.12, frag.y / uRes.y) ; // soften very bottom
  float a = line * uStrength * mask * mix(0.6, 1.0, edge);
  a += 0.035 * n * mask;                          // faint tonal body
  a += (hash(frag + uTime) - 0.5) * 0.02;         // film grain
  a = clamp(a, 0.0, 1.0);
  outColor = vec4(uFg * a, a);
}`;

function compile(gl, type, src) {
  const s = gl.createShader(type);
  gl.shaderSource(s, src);
  gl.compileShader(s);
  if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) {
    // eslint-disable-next-line no-console
    console.warn(gl.getShaderInfoLog(s));
    return null;
  }
  return s;
}

export default function ShaderBackground({
  dark = false,
  strength = 0.5,
  lines = 14,
  fadeLeft = 0,
  orderRef = null,
  className = "",
}) {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const gl = canvas.getContext("webgl2", { alpha: true, premultipliedAlpha: true, antialias: false });
    if (!gl) return undefined; // graceful fallback: plain CSS background

    const vs = compile(gl, gl.VERTEX_SHADER, VERT);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return undefined;
    const prog = gl.createProgram();
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return undefined;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    const loc = gl.getAttribLocation(prog, "p");
    gl.enableVertexAttribArray(loc);
    gl.vertexAttribPointer(loc, 2, gl.FLOAT, false, 0, 0);

    const U = (n) => gl.getUniformLocation(prog, n);
    const uRes = U("uRes"), uTime = U("uTime"), uMouse = U("uMouse"), uFg = U("uFg");
    const uOrder = U("uOrder"), uLines = U("uLines"), uStrength = U("uStrength"), uFadeLeft = U("uFadeLeft");
    gl.uniform3f(uFg, ...(dark ? [1, 1, 1] : [0, 0, 0]));
    gl.uniform1f(uLines, lines);
    gl.uniform1f(uStrength, strength);
    gl.uniform1f(uFadeLeft, fadeLeft);

    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const dpr = Math.min(window.devicePixelRatio || 1, 1.5);
    let visible = true, raf = 0;
    let so = orderRef ? orderRef.current : -1;
    const mouse = { x: 0.65, y: 0.5, tx: 0.65, ty: 0.5 };
    const t0 = performance.now();

    const resize = () => {
      const w = Math.max(1, Math.floor(canvas.clientWidth * dpr));
      const h = Math.max(1, Math.floor(canvas.clientHeight * dpr));
      if (canvas.width !== w || canvas.height !== h) {
        canvas.width = w;
        canvas.height = h;
      }
      gl.viewport(0, 0, w, h);
      gl.uniform2f(uRes, w, h);
    };

    const draw = (now) => {
      mouse.x += (mouse.tx - mouse.x) * 0.05;
      mouse.y += (mouse.ty - mouse.y) * 0.05;
      gl.uniform2f(uMouse, mouse.x, mouse.y);
      if (orderRef) so += (orderRef.current - so) * 0.07;
      gl.uniform1f(uOrder, orderRef ? so : -1);
      gl.uniform1f(uTime, reduce ? 12 : (now - t0) / 1000);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.drawArrays(gl.TRIANGLES, 0, 3);
    };

    const loop = (now) => {
      if (visible) draw(now);
      raf = requestAnimationFrame(loop);
    };

    const onMove = (e) => {
      const r = canvas.getBoundingClientRect();
      mouse.tx = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width));
      mouse.ty = Math.min(1, Math.max(0, 1 - (e.clientY - r.top) / r.height));
    };

    resize();
    const ro = new ResizeObserver(() => { resize(); if (reduce) draw(performance.now()); });
    ro.observe(canvas);
    const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0 });
    io.observe(canvas);
    if (reduce) {
      draw(performance.now());
      if (orderRef) window.addEventListener("scroll", () => { so = orderRef.current; draw(performance.now()); }, { passive: true });
    } else {
      window.addEventListener("pointermove", onMove, { passive: true });
      raf = requestAnimationFrame(loop);
    }

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("pointermove", onMove);
      ro.disconnect();
      io.disconnect();
    };
  }, [dark, strength, lines, fadeLeft, orderRef]);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      data-testid="shader-bg"
      className={`pointer-events-none absolute inset-0 h-full w-full ${className}`}
    />
  );
}
